import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  ChannelGroupListColumns,
  useChannelGroupListModel
} from './ChannelGroupListModel';
import {
  ChannelGroupListDTO,
  ChannelGroupStatusFilter
} from 'core/channelGroup/ChannelGroup';
import i18n from 'i18n';
import { useCallback, useMemo } from 'react';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { SortOrder } from 'components/common/Table/types';
import { Page } from 'components/Page/Page';
import { FilterMenuTab } from 'components/common/FilterMenuTab/FilterMenuTab';

export type ChannelGroupListProps = {
  channelGroups: ChannelGroupListDTO[];
  statusFilter: ChannelGroupStatusFilter;
  onStatusFilterChange: (status: ChannelGroupStatusFilter) => void;
};

export const ChannelGroupList: React.FC<ChannelGroupListProps> = ({
  channelGroups,
  statusFilter,
  onStatusFilterChange
}) => {
  const {
    searchString,
    filteredList,
    columns,
    filterMenuTabConfigs,
    onSearch
  } = useChannelGroupListModel(
    channelGroups,
    statusFilter,
    onStatusFilterChange
  );

  const listDefaultSorted = useMemo(
    () =>
      [
        {
          dataField: ChannelGroupListColumns.ID.toString(),
          order: 'desc'
        }
      ] as [{ dataField: any; order: SortOrder }],
    []
  );

  // 篩選器放進表格的操作列插槽，會與內建的 SearchBar 並排在同一行
  // （`StickableTanstackTable` 本身沒有 filter 相關 props）。
  const renderListOperations = useCallback(
    () => [
      <FilterMenuTab
        key='status-filter'
        filterMenuTabConfigs={filterMenuTabConfigs}
      />
    ],
    [filterMenuTabConfigs]
  );

  const renderRowBtns = useCallback((channelGroupList: ChannelGroupListDTO) => {
    return [
      <IconWithTooltip
        key={channelGroupList.id}
        icon={faPencilAlt}
        tooltipProps={{
          id: `channel-groups-edit-tip-${channelGroupList.id}`,
          tooltip: i18n.t('channelGroupList.hints.edit'),
          link: `/system/channel-groups/${channelGroupList.id}/edit`
        }}
      />
    ];
  }, []);

  return (
    <div style={{ height: '100%' }}>
      <Page title={i18n.t('channelGroups.title')}>
        <StickableTanstackTable
          keyField={ChannelGroupListColumns.ID}
          customPadding={false}
          data={filteredList}
          columns={columns}
          defaultSorted={listDefaultSorted}
          noDataIndication={i18n.t('channelGroupList.labels.noData')}
          defaultSearchString={searchString}
          onHandleSearch={onSearch}
          searchbarPlaceholder={i18n.t(
            'channelGroupList.placeholders.searchbar'
          )}
          renderRowBtns={renderRowBtns}
          renderListOperations={renderListOperations}
        />
      </Page>
    </div>
  );
};
